import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BookingNote, Request as DashboardRequest } from '@transreport/pauk-rrms-api'
import {
  assignCallsignToAssistanceDetail,
  getShiftTimeline,
  getGetBookingNotesQueryKey,
  searchRequests,
  useCreateBookingNote,
  useGetBookingNotes,
  useGetStationSetting,
  unassignCallsignFromAssistanceDetail,
  useShowAssistanceChecklist,
} from '@transreport/pauk-rrms-api'
import { addDays, format, isValid, parse, parseISO } from 'date-fns'
import {
  AlertCircle,
  CircleCheck,
  ClipboardList,
  Info,
  Link2,
  Mail,
  MessageSquarePlus,
  RefreshCw,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { DataTableColumn } from '@/components/framework'
import { AppButton, DataTable, SkeletonBlock } from '@/components/framework'
import { ValidationReminderModal } from '@/components/shared'
import { AddAssistanceNoteModal } from '@/components/shared/AddAssistanceNoteModal'
import { CompleteChecklistModal } from '@/components/shared/CompleteChecklistModal'
import { EmailManagerModal } from '@/components/shared/EmailManagerModal'
import { LinkedJobsContext } from '@/components/shared/LinkedJobsContext'
import { RequestManagementModal } from '@/components/shared/RequestManagementModal'
import { useOperatingStations } from '@/hooks/useOperatingStations'
import { trackEvent } from '@/lib/analytics'
import { getApiErrorMessage } from '@/lib/apiErrors'
import { getBookingUrl } from '@/lib/bookingLinks'
import { buildLinkedJobContexts } from '@/lib/linkedJobs'
import { defineMutationAnalytics } from '@/lib/queryClient'
import {
  type DashboardRequestsFilters,
  useDashboardRequestsStore,
} from '@/lib/stores/dashboardRequests'
import { resolveAssistanceTiming } from '@/lib/timeline'
import { UNASSIGNED_ROW_ID } from '@/lib/timeline/constants'
import {
  getRealtimeSeverity,
  hasPlatformChangeSignal,
  isJourneyLate,
} from '@/lib/timeline/realtimeClassification'
import { shiftTimelineToCallsignRows } from '@/lib/timeline/shiftTimelineModel'
import type { ChecklistRequest, CallsignRow, LinkedJobContext } from '@/types/timeline'

import { AppTooltip } from '../../framework/AppTooltip'
import { DashboardBookingsTableSkeleton } from './DashboardBookingsTableSkeleton'

type CreateBookingNoteVariables = Parameters<ReturnType<typeof useCreateBookingNote>['mutate']>[0]
type AssignCallsignMutationVariables = {
  callsignId: string
  assistanceDetailId: string
  callsignName: string
  staffName?: string
  isReassignment: boolean
}

export interface BookingRow {
  id: string
  paRef: string
  passenger: string
  jobType: string
  fromCode: string
  fromName: string
  toCode: string
  toName: string
  journeyDate: string
  time: string
  platform: string
  headcode: string
  rsid: string
  assistanceDetails: string
  bookingId: string
  assistanceId: string
  bookingStatus: string
  assistanceStatus: string
  assignTo: string
  assignToStaffName?: string
  actions?: React.ReactNode
  requestChecklist?: React.ReactNode
  accessConsiderations: string
  realtimeClassification?: string
  realtimeTime?: string
  isRealtimeAdjusted?: boolean
  linkedJobContext?: LinkedJobContext
}

type SortDirection = 'asc' | 'desc'
type SortableBookingColumnKey = 'status' | 'passenger' | 'requestType' | 'journeyDate'

const TABLE_ACTION_BUTTON_CLASS =
  'min-h-0 whitespace-nowrap rounded-md bg-secondary/80 px-2 py-1 text-sm leading-4 shadow-sm hover:bg-secondary/90 active:bg-secondary/90 disabled:bg-secondary/80'
const TABLE_STACKED_ACTION_BUTTON_CLASS = `${TABLE_ACTION_BUTTON_CLASS} flex items-center gap-1.5`

const SORTABLE_BOOKING_COLUMNS: readonly SortableBookingColumnKey[] = [
  'status',
  'passenger',
  'requestType',
  'journeyDate',
]

const ASSISTANCE_STATUS_SORT_RANK: Record<string, number> = {
  open: 1,
  pending: 2,
  incomplete: 3,
  completed: 4,
  cancelled: 5,
}

function normalizeSortValue(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

function getAssistanceStatusSortRank(status: string): number {
  return ASSISTANCE_STATUS_SORT_RANK[status] ?? 99
}

function parseJourneyDateForSort(value: string): number | null {
  if (!value?.trim()) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

function isSortableBookingColumnKey(key: string): key is SortableBookingColumnKey {
  return SORTABLE_BOOKING_COLUMNS.includes(key as SortableBookingColumnKey)
}
const RequestChecklistCell: React.FC<{
  assistanceId: string
  onOpen: () => void
}> = ({ assistanceId, onOpen }) => {
  const { t } = useTranslation()

  const checklistQuery = useShowAssistanceChecklist(assistanceId, {
    query: { enabled: Boolean(assistanceId), staleTime: 60_000 },
  })

  if (checklistQuery.isPending) {
    return (
      <div role="status" aria-label={t('checklistModal.loading')} className="flex justify-center">
        <span className="sr-only">{t('checklistModal.loading')}</span>
        <SkeletonBlock className="h-8 w-24" />
      </div>
    )
  }

  if (checklistQuery.data?.completed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs leading-4 whitespace-nowrap text-gray-700">
        <CircleCheck size={14} aria-hidden />
        <span>{t('dashboard.requestChecklist.completed')}</span>
      </span>
    )
  }

  return (
    <AppButton
      variant="secondary"
      size="sm"
      onClick={onOpen}
      ariaLabel={t('dashboard.requestChecklist.startAriaLabel')}
      buttonClass={TABLE_STACKED_ACTION_BUTTON_CLASS}
    >
      <ClipboardList size={14} aria-hidden />
      <span>{t('dashboard.requestChecklist.start')}</span>
    </AppButton>
  )
}

const JourneyIndicatorBadge: React.FC<{
  tone: 'late' | 'platform'
  children: React.ReactNode
}> = ({ tone, children }) => {
  const toneClass =
    tone === 'late'
      ? 'border-slate-500 bg-white text-slate-800'
      : 'border-violet-500 bg-violet-50 text-violet-800'

  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs leading-4 font-bold tracking-normal whitespace-nowrap uppercase ${toneClass}`}
    >
      {children}
    </span>
  )
}

const RealtimeTimeCell: React.FC<{ row: BookingRow }> = ({ row }) => {
  const { t } = useTranslation()
  const severity = getRealtimeSeverity(row.realtimeClassification)
  const isLate = isJourneyLate(row.realtimeClassification)
  const hasDisruptionAlert =
    severity !== 'normal' && severity !== 'delayed' && Boolean(row.realtimeTime)
  const showRealtimeTime = Boolean(
    row.realtimeTime && (!row.isRealtimeAdjusted || row.realtimeTime !== row.time)
  )
  const alertColour = 'text-danger'
  const classificationLabel = row.realtimeClassification
    ? t(`realtimeClassification.${row.realtimeClassification}`, { defaultValue: '' })
    : ''
  return (
    <div className="flex flex-col items-center gap-0.5 text-center">
      <span
        className={`text-sm ${!row.isRealtimeAdjusted && (isLate || hasDisruptionAlert) ? 'text-gray-600' : 'text-gray-900'}`}
      >
        {row.time || '-'}
      </span>
      {isLate && (
        <span className="flex items-center justify-center gap-1.5">
          <JourneyIndicatorBadge tone="late">
            {t('journeyIndicators.lateShort')}
          </JourneyIndicatorBadge>
          {showRealtimeTime && (
            <span className="text-sm font-bold text-slate-800">{row.realtimeTime}</span>
          )}
        </span>
      )}
      {hasDisruptionAlert && (
        <AppTooltip content={classificationLabel || undefined}>
          <span
            className={`flex cursor-default items-center gap-1 text-sm font-bold ${alertColour}`}
          >
            <AlertCircle size={12} aria-hidden="true" />
            {row.realtimeTime}
          </span>
        </AppTooltip>
      )}
    </div>
  )
}

const PlatformCell: React.FC<{ row: BookingRow }> = ({ row }) => {
  const { t } = useTranslation()
  const showPlatformChange = hasPlatformChangeSignal(row.realtimeClassification, row.platform)
  const classificationLabel = row.realtimeClassification
    ? t(`realtimeClassification.${row.realtimeClassification}`, { defaultValue: '' })
    : ''
  return (
    <div className="flex items-center justify-center gap-1.5 text-center">
      <span className="text-sm text-gray-900">{row.platform || '-'}</span>
      {showPlatformChange &&
        (classificationLabel ? (
          <AppTooltip content={classificationLabel}>
            <span className="inline-flex cursor-default items-center">
              <JourneyIndicatorBadge tone="platform">
                {t('journeyIndicators.platformChangedTable')}
              </JourneyIndicatorBadge>
            </span>
          </AppTooltip>
        ) : (
          <JourneyIndicatorBadge tone="platform">
            {t('journeyIndicators.platformChangedTable')}
          </JourneyIndicatorBadge>
        ))}
    </div>
  )
}

const RequestTypeCell: React.FC<{ row: BookingRow }> = ({ row }) => {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-center gap-2">
      <span
        className={`inline-flex min-w-10 justify-center rounded px-2 py-0.5 text-xs font-semibold ${
          row.jobType === 'ARR'
            ? 'bg-arrival-bg text-arrival-text'
            : row.jobType === 'DEP'
              ? 'bg-departure-bg text-departure-text'
              : 'bg-gray-100 text-gray-800'
        }`}
      >
        {row.jobType}
      </span>
      {row.linkedJobContext && (
        <AppTooltip content={<LinkedJobsContext context={row.linkedJobContext} />}>
          <button
            type="button"
            aria-label={t('linkedJobs.indicatorAriaLabel')}
            className="inline-flex min-h-6 min-w-6 cursor-help items-center justify-center rounded text-primary focus-visible:ring-2 focus-visible:ring-primary-focus focus-visible:outline-none"
          >
            <Link2 size={14} aria-hidden="true" />
          </button>
        </AppTooltip>
      )}
    </div>
  )
}

const UnconfirmedBadge: React.FC = () => {
  const { t } = useTranslation()
  return (
    <span className="text-xs font-semibold text-red-600 uppercase">
      {t('dashboard.badges.unconfirmed')}
    </span>
  )
}

function toTitleCaseStatus(status: string): string {
  return status
    .trim()
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const AssistanceStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const normalizedStatus = status.trim().toLowerCase()

  const toneClasses =
    normalizedStatus === 'completed'
      ? 'border-green-200 bg-green-50 text-green-700'
      : normalizedStatus === 'pending' || normalizedStatus === 'open'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : normalizedStatus === 'incomplete'
          ? 'border-red-200 bg-red-50 text-red-700'
          : normalizedStatus === 'cancelled'
            ? 'border-gray-300 bg-gray-100 text-gray-700'
            : 'border-gray-200 bg-gray-50 text-gray-700'

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${toneClasses}`}
    >
      {toTitleCaseStatus(status)}
    </span>
  )
}

function requestToBookingRow(
  request: DashboardRequest,
  autoUpdateAssistanceJobTime = false
): BookingRow {
  const {
    id,
    type,
    booking,
    passenger_name,
    from_station,
    to_station,
    scheduled_date,
    scheduled_time,
    journey_leg,
    details,
    callsign_assignment,
    status,
    access_considerations,
    booking_status,
  } = request

  const accessConsiderations = access_considerations?.join(', ')
  const platform = type === 'DEP' ? journey_leg.board_platform : journey_leg.alight_platform
  const realtimeTime =
    type === 'DEP'
      ? (journey_leg.realtime_departure ?? undefined)
      : (journey_leg.realtime_arrival ?? undefined)
  const resolvedTiming = resolveAssistanceTiming({
    type,
    startTime: scheduled_time ?? '',
    endTime: scheduled_time ?? '',
    scheduledTrainTime: scheduled_time,
    realtimeTrainTime: realtimeTime,
    autoUpdateAssistanceJobTime,
  })

  const row: BookingRow = {
    id,
    paRef: booking.ref,
    passenger: passenger_name,
    bookingId: booking.id,
    assistanceId: id,
    jobType: type,
    fromCode: from_station?.crs_code ?? '',
    fromName: from_station?.name ?? '',
    toCode: to_station?.crs_code ?? '',
    toName: to_station?.name ?? '',
    journeyDate: scheduled_date ?? '',
    time: resolvedTiming.startTime,
    platform: platform ?? '',
    headcode: journey_leg.head_code ?? '',
    rsid: journey_leg.rsid ?? '',
    assistanceDetails: details.join(', '),
    accessConsiderations,
    bookingStatus: booking_status,
    assistanceStatus: status,
    assignTo: callsign_assignment ?? '',
    realtimeClassification: journey_leg.realtime_classification,
    realtimeTime: resolvedTiming.realtimeTime,
    isRealtimeAdjusted: resolvedTiming.isRealtimeAdjusted,
  }

  return row
}

const BOOKING_COLUMNS: DataTableColumn<BookingRow>[] = [
  {
    key: 'paRef',
    header: 'dashboard.columns.paRef',
    render: (row) => {
      const bookingUrl = getBookingUrl(row.paRef)

      return (
        <div className="flex flex-col items-center gap-1 text-center">
          {bookingUrl ? (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium whitespace-nowrap text-primary underline"
            >
              {row.paRef}
            </a>
          ) : (
            <span className="text-sm font-medium whitespace-nowrap text-gray-900">{row.paRef}</span>
          )}
          {row.bookingStatus === 'unconfirmed' && <UnconfirmedBadge />}
        </div>
      )
    },
  },
  {
    key: 'passenger',
    header: 'dashboard.columns.passenger',
    render: (row) => (
      <div className="flex min-h-8 items-center justify-center gap-2 text-center">
        <span className="text-sm font-medium whitespace-nowrap text-gray-900">{row.passenger}</span>
        {row.accessConsiderations && (
          <AppTooltip content={row.accessConsiderations}>
            <Info size={14} aria-hidden="true" className="cursor-help text-gray-500" />
          </AppTooltip>
        )}
      </div>
    ),
  },
  {
    key: 'requestType',
    header: 'dashboard.columns.requestType',
    render: (row) => <RequestTypeCell row={row} />,
  },
  {
    key: 'fromCode',
    header: 'dashboard.columns.from',
    render: (row) => (
      <div className="flex flex-col items-center gap-1 text-center">
        <a
          href={row.fromCode ? `https://www.nationalrail.co.uk/stations/${row.fromCode}` : '#'}
          target="_blank"
          rel="noreferrer"
          className={`inline-flex max-w-max items-center text-sm font-medium ${
            row.jobType === 'DEP'
              ? 'rounded bg-brand-surface px-2 py-0.5 text-brand-ink underline'
              : 'text-gray-900'
          }`}
        >
          {row.fromCode || row.fromName}
        </a>
      </div>
    ),
  },
  {
    key: 'toCode',
    header: 'dashboard.columns.to',
    render: (row) => (
      <div className="flex flex-col items-center gap-1 text-center">
        <a
          href={row.toCode ? `https://www.nationalrail.co.uk/stations/${row.toCode}` : '#'}
          target="_blank"
          rel="noreferrer"
          className={`inline-flex max-w-max items-center text-sm font-medium ${
            row.jobType === 'ARR'
              ? 'rounded bg-brand-surface px-2 py-0.5 text-brand-ink underline'
              : 'text-gray-900'
          }`}
        >
          {row.toCode || row.toName}
        </a>
      </div>
    ),
  },
  { key: 'journeyDate', header: 'dashboard.columns.journeyDate' },
  {
    key: 'time',
    header: 'dashboard.columns.time',
    render: (row) => <RealtimeTimeCell row={row} />,
  },
  {
    key: 'platform',
    header: 'dashboard.columns.platform',
    render: (row) => <PlatformCell row={row} />,
  },
  { key: 'headcode', header: 'dashboard.columns.headcode' },
  { key: 'rsid', header: 'dashboard.columns.rsid' },
  {
    key: 'status',
    header: 'dashboard.columns.status',
    render: (row) => {
      const status = row.assistanceStatus || row.bookingStatus

      if (!status?.trim()) {
        return <span className="text-sm text-gray-900">-</span>
      }

      return <AssistanceStatusBadge status={status} />
    },
  },
  {
    key: 'assistanceDetails',
    header: 'dashboard.columns.assistanceDetails',
    render: (row) => {
      const details = (row.assistanceDetails ?? '').trim()

      return (
        <div className="flex items-center justify-center">
          {details ? (
            <AppTooltip content={details}>
              <Info size={14} aria-hidden="true" className="cursor-help text-gray-500" />
            </AppTooltip>
          ) : (
            <span className="text-sm text-gray-900">-</span>
          )}
        </div>
      )
    },
  },
  {
    key: 'assignTo',
    header: 'dashboard.columns.assignTo',
  },
  {
    key: 'requestChecklist',
    header: 'dashboard.columns.requestChecklist',
    render: (row) => (
      <div className="flex w-full items-center justify-center whitespace-nowrap">
        {row.requestChecklist ?? null}
      </div>
    ),
  },
  {
    key: 'actions',
    header: 'common.actions',
    render: (row) => row.actions ?? null,
  },
]

function buildDateTimeParam(dateTimeValue: string | undefined): string {
  if (!dateTimeValue) return ''

  const normalized = dateTimeValue.length === 16 ? `${dateTimeValue}:00` : dateTimeValue
  const parsedDateTime = parseISO(normalized)
  if (!isValid(parsedDateTime)) return ''

  return format(parsedDateTime, "yyyy-MM-dd'T'HH:mm:ss")
}

function buildCallsignLookupKey(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return trimmed.split(/\s|-/)[0]?.toLowerCase() ?? ''
}

function resolveCallsignIdByAssignmentLabel(
  callsigns: CallsignRow[],
  assignmentLabel: string
): string {
  const assignedText = assignmentLabel.trim()
  if (!assignedText) return ''

  const byName = callsigns.find(
    (callsign) =>
      callsign.name === assignedText ||
      assignedText.startsWith(`${callsign.name} `) ||
      assignedText.startsWith(`${callsign.name}-`)
  )

  return byName?.id ?? ''
}

type TimelineAssignment = {
  callsignName: string
  staffName?: string
}

function buildTimelineAssignmentLookup(callsigns: CallsignRow[]): {
  byAssistanceId: Map<string, TimelineAssignment>
  byPaRef: Map<string, TimelineAssignment>
} {
  const byAssistanceId = new Map<string, TimelineAssignment>()
  const byPaRef = new Map<string, TimelineAssignment>()

  for (const callsign of callsigns) {
    if (callsign.id === UNASSIGNED_ROW_ID) continue

    const assignment: TimelineAssignment = {
      callsignName: callsign.name,
      staffName: callsign.staffName?.trim() || undefined,
    }

    for (const request of callsign.requests) {
      byAssistanceId.set(request.id, assignment)
      if (request.ref) {
        byPaRef.set(request.ref, assignment)
      }
    }
  }

  return { byAssistanceId, byPaRef }
}

function resolveAssignedCallsignId(
  callsigns: CallsignRow[],
  assistanceId: string,
  assignmentLabel?: string
): string {
  const fromTimeline = callsigns.find((callsign) =>
    callsign.requests.some((request) => request.id === assistanceId)
  )?.id

  if (fromTimeline) return fromTimeline
  if (!assignmentLabel) return ''

  return resolveCallsignIdByAssignmentLabel(callsigns, assignmentLabel)
}

type AppliedDashboardRequestsFilters = DashboardRequestsFilters & {
  station_code: string
}

type OptimisticAssignment = {
  callsignName: string | null
  staffName?: string
  mutationId: number
}

export const DashboardBookingsSection: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [appliedFilters, setAppliedFilters] = useState<AppliedDashboardRequestsFilters | null>(null)
  const lastAutoLoadedStationRef = useRef<string | null>(null)
  const { selectedStationCode } = useOperatingStations()
  const stationSettingQuery = useGetStationSetting(selectedStationCode, {
    query: { enabled: Boolean(selectedStationCode) },
  })
  const autoUpdateAssistanceJobTime =
    stationSettingQuery.data?.auto_update_assistance_job_time ?? false

  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false)
  const [noteBookingId, setNoteBookingId] = useState('')
  const [isEmailManagerOpen, setIsEmailManagerOpen] = useState(false)
  const [selectedActionRow, setSelectedActionRow] = useState<BookingRow | null>(null)
  const [isChecklistOpen, setIsChecklistOpen] = useState(false)
  const [checklistRequest, setChecklistRequest] = useState<ChecklistRequest | null>(null)
  const [optimisticAssignments, setOptimisticAssignments] = useState<
    Record<string, OptimisticAssignment>
  >({})
  const optimisticAssignmentIdRef = useRef(0)
  const [sortState, setSortState] = useState<{
    key: SortableBookingColumnKey
    direction: SortDirection
  } | null>(null)

  const filters = useDashboardRequestsStore((s) => s.filters)
  const setFilter = useDashboardRequestsStore((s) => s.setFilter)
  const setFilters = useDashboardRequestsStore((s) => s.setFilters)
  const clearFilters = useDashboardRequestsStore((s) => s.clearFilters)

  const clearOptimisticAssignment = (assistanceDetailId: string, mutationId: number) => {
    setOptimisticAssignments((current) => {
      if (current[assistanceDetailId]?.mutationId !== mutationId) return current
      const next = { ...current }
      delete next[assistanceDetailId]
      return next
    })
  }

  const {
    data: jobs = [],
    isLoading,
    error,
  } = useQuery<DashboardRequest[]>({
    queryKey: ['dashboardRequests', appliedFilters],
    queryFn: () => {
      if (!appliedFilters) {
        return Promise.resolve([] as DashboardRequest[])
      }

      const { station_code, query, date_from, date_to } = appliedFilters

      return searchRequests({
        station_code,
        query,
        date_from,
        date_to,
        category: appliedFilters.category,
      })
    },
    enabled: Boolean(appliedFilters?.station_code),
  })

  const bookingId = noteBookingId

  const bookingLogQuery = useGetBookingNotes(
    bookingId,
    selectedStationCode ? { station_code: selectedStationCode } : { station_code: '' },
    {
      query: {
        enabled: isAddNoteOpen && Boolean(bookingId && selectedStationCode),
        staleTime: 60_000,
      },
    }
  )

  const createBookingNoteMutation = useCreateBookingNote({
    mutation: {
      meta: {
        analytics: defineMutationAnalytics<CreateBookingNoteVariables, 'note_added'>({
          event: 'note_added',
          area: 'requests',
          parameters: (variables) => ({ station_code: variables.params.station_code }),
        }),
        toast: {
          success: t('assistanceNotes.saveSuccess'),
          error: t('assistanceNotes.saveError'),
        },
      },
      onMutate: async (variables) => {
        if (!variables?.id || !variables?.params?.station_code) return
        const key = getGetBookingNotesQueryKey(variables.id, {
          station_code: variables.params.station_code,
        })

        await queryClient.cancelQueries({ queryKey: key })

        const previous = queryClient.getQueryData<BookingNote[]>(key) ?? []

        const optimistic: BookingNote = {
          id: `optimistic-${Date.now()}`,
          text: variables.data?.text ?? '',
          created_at: new Date().toISOString(),
          created_by: undefined,
        }

        queryClient.setQueryData<BookingNote[]>(key, [optimistic, ...previous])

        return { key, previous }
      },
      onError: (_error, _variables, context) => {
        if (!context?.key) return
        queryClient.setQueryData(context.key, context.previous)
      },
      onSuccess: (_data, variables) => {
        if (!variables?.id || !variables?.params?.station_code) return

        void queryClient.invalidateQueries({
          queryKey: getGetBookingNotesQueryKey(variables.id, {
            station_code: variables.params.station_code,
          }),
        })
        // Refresh the dashboard row data so note-related state updates immediately.
        void queryClient.invalidateQueries({ queryKey: ['dashboardRequests'] })
      },
      onSettled: (_data, _error, variables) => {
        if (!variables?.id || !variables?.params?.station_code) return
        void queryClient.invalidateQueries({
          queryKey: getGetBookingNotesQueryKey(variables.id, {
            station_code: variables.params.station_code,
          }),
        })
      },
    },
  })

  const displayDateParam = useMemo(() => {
    if (!filters.date_from) return null

    const parsedDate = parse(filters.date_from.slice(0, 10), 'yyyy-MM-dd', new Date())
    if (!isValid(parsedDate)) return null

    return format(parsedDate, 'dd-MM-yyyy')
  }, [filters.date_from])

  const callsignDisplayRowsQuery = useQuery({
    queryKey: [
      'dashboardCallsignsDisplay',
      displayDateParam,
      selectedStationCode,
      autoUpdateAssistanceJobTime,
    ] as const,
    queryFn: async () => {
      if (!displayDateParam || !selectedStationCode) return [] as CallsignRow[]

      const timeline = await getShiftTimeline({
        date: displayDateParam,
        station_code: selectedStationCode,
        query: undefined,
      })

      return shiftTimelineToCallsignRows(timeline ?? null, selectedStationCode, {
        autoUpdateAssistanceJobTime,
      })
    },
    enabled: Boolean(displayDateParam && selectedStationCode),
    staleTime: 30_000,
  })

  const callsignStaffByName = useMemo(() => {
    const map = new Map<string, string>()

    for (const row of callsignDisplayRowsQuery.data ?? []) {
      const key = buildCallsignLookupKey(row.name)
      const staffName = row.staffName?.trim()

      if (!key || !staffName) continue
      map.set(key, staffName)
    }

    return map
  }, [callsignDisplayRowsQuery.data])

  const timelineAssignments = useMemo(
    () => buildTimelineAssignmentLookup(callsignDisplayRowsQuery.data ?? []),
    [callsignDisplayRowsQuery.data]
  )

  const bookings: BookingRow[] = useMemo(() => {
    const baseBookings = jobs.map((job) => requestToBookingRow(job, autoUpdateAssistanceJobTime))
    const linkedJobContexts = buildLinkedJobContexts(
      baseBookings.map((booking) => ({
        id: booking.assistanceId,
        bookingId: booking.bookingId,
        type: booking.jobType,
        startTime: booking.time,
        fromCode: booking.fromCode,
        toCode: booking.toCode,
      }))
    )

    return baseBookings.map((base) => {
      const timelineAssignment = base.assignTo.trim()
        ? undefined
        : (timelineAssignments.byAssistanceId.get(base.assistanceId) ??
          (base.paRef ? timelineAssignments.byPaRef.get(base.paRef) : undefined))
      const hasOptimisticAssignment = Object.hasOwn(optimisticAssignments, base.assistanceId)
      const optimisticAssignment = optimisticAssignments[base.assistanceId]
      const assignTo = hasOptimisticAssignment
        ? (optimisticAssignment?.callsignName ?? '')
        : base.assignTo || timelineAssignment?.callsignName || ''
      const assignToKey = buildCallsignLookupKey(assignTo)
      const staffName = hasOptimisticAssignment
        ? optimisticAssignment?.staffName
        : (timelineAssignment?.staffName ??
          (assignToKey ? callsignStaffByName.get(assignToKey)?.trim() : undefined))

      return {
        ...base,
        assignTo,
        assignToStaffName: staffName,
        linkedJobContext: linkedJobContexts.get(base.assistanceId),
        requestChecklist: (
          <RequestChecklistCell
            assistanceId={base.assistanceId}
            onOpen={() => {
              setChecklistRequest({
                id: base.assistanceId,
                type: base.jobType === 'DEP' ? 'DEP' : 'ARR',
              })
              setIsChecklistOpen(true)
            }}
          />
        ),
        actions: (
          <div className="flex items-center justify-center gap-1.5">
            <AppButton
              variant="secondary"
              size="sm"
              ariaLabel={t('assistanceDetails.actions.addNote')}
              buttonClass={TABLE_STACKED_ACTION_BUTTON_CLASS}
              onClick={() => {
                setNoteBookingId(base.bookingId)
                setSelectedActionRow(base)
                setIsAddNoteOpen(true)
              }}
            >
              <MessageSquarePlus size={14} aria-hidden className="shrink-0" />
              {t('assistanceDetails.actions.note')}
            </AppButton>
            <AppButton
              variant="secondary"
              size="sm"
              ariaLabel={t('assistanceDetails.actions.emailManager')}
              buttonClass={TABLE_STACKED_ACTION_BUTTON_CLASS}
              onClick={() => {
                setSelectedActionRow(base)
                setIsEmailManagerOpen(true)
              }}
            >
              <Mail size={14} aria-hidden className="shrink-0" />
              {t('assistanceDetails.actions.emailManager')}
            </AppButton>
          </div>
        ),
      }
    })
  }, [
    autoUpdateAssistanceJobTime,
    callsignStaffByName,
    jobs,
    optimisticAssignments,
    t,
    timelineAssignments,
  ])

  const sortedBookings: BookingRow[] = useMemo(() => {
    if (!sortState) return bookings

    const factor = sortState.direction === 'asc' ? 1 : -1
    const rowsWithIndex = bookings.map((row, index) => ({ row, index }))

    rowsWithIndex.sort((a, b) => {
      if (sortState.key === 'status') {
        const aStatus = normalizeSortValue(a.row.assistanceStatus)
        const bStatus = normalizeSortValue(b.row.assistanceStatus)
        const rankDelta =
          getAssistanceStatusSortRank(aStatus) - getAssistanceStatusSortRank(bStatus)
        if (rankDelta !== 0) return rankDelta * factor

        const textDelta = aStatus.localeCompare(bStatus, undefined, { sensitivity: 'base' })
        if (textDelta !== 0) return textDelta * factor
      }

      if (sortState.key === 'passenger') {
        const aPassenger = normalizeSortValue(a.row.passenger)
        const bPassenger = normalizeSortValue(b.row.passenger)
        const delta = aPassenger.localeCompare(bPassenger, undefined, { sensitivity: 'base' })
        if (delta !== 0) return delta * factor
      }

      if (sortState.key === 'requestType') {
        const aRequestType = normalizeSortValue(a.row.jobType)
        const bRequestType = normalizeSortValue(b.row.jobType)
        const delta = aRequestType.localeCompare(bRequestType, undefined, { sensitivity: 'base' })
        if (delta !== 0) return delta * factor
      }

      if (sortState.key === 'journeyDate') {
        const aDate = parseJourneyDateForSort(a.row.journeyDate)
        const bDate = parseJourneyDateForSort(b.row.journeyDate)

        if (aDate === null && bDate !== null) return 1
        if (aDate !== null && bDate === null) return -1
        if (aDate !== null && bDate !== null) {
          const delta = aDate - bDate
          if (delta !== 0) return delta * factor
        }
      }

      return a.index - b.index
    })

    return rowsWithIndex.map(({ row }) => row)
  }, [bookings, sortState])
  const [selectedRow, setSelectedRow] = useState<BookingRow | null>(null)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false)
  const [isValidationReminderOpen, setIsValidationReminderOpen] = useState(false)

  const selectedRowDateParam = useMemo(() => {
    if (!selectedRow?.journeyDate) return null
    try {
      return format(parseISO(selectedRow.journeyDate), 'dd-MM-yyyy')
    } catch {
      return null
    }
  }, [selectedRow?.journeyDate])

  const callsignsQuery = useQuery({
    queryKey: [
      'dashboardCallsigns',
      selectedRowDateParam,
      selectedStationCode,
      autoUpdateAssistanceJobTime,
    ] as const,
    queryFn: async () => {
      if (!selectedRowDateParam || !selectedStationCode) return [] as CallsignRow[]
      const timeline = await getShiftTimeline({
        date: selectedRowDateParam,
        station_code: selectedStationCode,
        query: undefined,
      })
      return shiftTimelineToCallsignRows(timeline ?? null, selectedStationCode, {
        autoUpdateAssistanceJobTime,
      })
    },
    enabled: Boolean(
      (isAssignModalOpen || isReassignModalOpen || isEmailManagerOpen) &&
      selectedRowDateParam &&
      selectedStationCode
    ),
    staleTime: 30_000,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
  })

  const callsigns = useMemo(
    () => (callsignsQuery.data ?? []).filter((c) => c.id !== UNASSIGNED_ROW_ID),
    [callsignsQuery.data]
  )

  const modalCallsigns = useMemo(() => {
    if (callsigns.length > 0) return callsigns
    return (callsignDisplayRowsQuery.data ?? []).filter((c) => c.id !== UNASSIGNED_ROW_ID)
  }, [callsignDisplayRowsQuery.data, callsigns])

  const assignMutation = useMutation({
    mutationKey: ['assignCallsignToAssistanceDetail'],
    meta: {
      analytics: defineMutationAnalytics<
        AssignCallsignMutationVariables,
        'request_assigned' | 'request_reassigned'
      >({
        event: (variables) =>
          variables.isReassignment ? 'request_reassigned' : 'request_assigned',
        area: 'requests',
      }),
    },
    mutationFn: (variables: AssignCallsignMutationVariables) =>
      assignCallsignToAssistanceDetail(variables.callsignId, {
        assistance_detail_id: variables.assistanceDetailId,
      }),
    onMutate: ({ assistanceDetailId, callsignName, staffName }) => {
      const mutationId = ++optimisticAssignmentIdRef.current
      setOptimisticAssignments((current) => ({
        ...current,
        [assistanceDetailId]: { callsignName, staffName, mutationId },
      }))
      return { mutationId }
    },
    onError: async (mutationError, { assistanceDetailId }, context) => {
      if (context) clearOptimisticAssignment(assistanceDetailId, context.mutationId)
      const fallback = t('requestAssign.assignError')
      toast.error((await getApiErrorMessage(mutationError)) ?? fallback)
    },
    onSettled: async (_data, _error, { assistanceDetailId }, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dashboardRequests'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboardCallsigns'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboardCallsignsDisplay'] }),
      ])
      if (context) clearOptimisticAssignment(assistanceDetailId, context.mutationId)
    },
  })

  const unassignMutation = useMutation({
    mutationKey: ['unassignCallsignFromAssistanceDetail'],
    mutationFn: ({
      callsignId,
      assistanceDetailId,
    }: {
      callsignId: string
      assistanceDetailId: string
    }) =>
      unassignCallsignFromAssistanceDetail(callsignId, {
        assistance_detail_id: assistanceDetailId,
      }),
    meta: {
      analytics: { event: 'request_unassigned', area: 'requests' },
      toast: { error: t('requestAssign.unassignError') },
    },
    onMutate: ({ assistanceDetailId }) => {
      const mutationId = ++optimisticAssignmentIdRef.current
      setOptimisticAssignments((current) => ({
        ...current,
        [assistanceDetailId]: { callsignName: null, mutationId },
      }))
      return { mutationId }
    },
    onError: (_error, { assistanceDetailId }, context) => {
      if (context) clearOptimisticAssignment(assistanceDetailId, context.mutationId)
    },
    onSettled: async (_data, _error, { assistanceDetailId }, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dashboardRequests'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboardCallsigns'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboardCallsignsDisplay'] }),
      ])
      if (context) clearOptimisticAssignment(assistanceDetailId, context.mutationId)
    },
  })

  const openAssignFlowForRow = (row: BookingRow) => {
    setSelectedRow(row)
    const bookingStatus = (row.bookingStatus ?? '').toLowerCase()
    if (bookingStatus === 'unconfirmed') {
      setIsValidationReminderOpen(true)
      return
    }
    setIsAssignModalOpen(true)
  }

  const openReassignFlowForRow = (row: BookingRow) => {
    setSelectedRow(row)
    setIsReassignModalOpen(true)
  }

  const currentCallsignIdForSelectedRow = useMemo(() => {
    if (!selectedRow?.assignTo) return ''

    return resolveAssignedCallsignId(
      [...callsigns, ...(callsignDisplayRowsQuery.data ?? [])],
      selectedRow.assistanceId,
      selectedRow.assignTo
    )
  }, [callsignDisplayRowsQuery.data, callsigns, selectedRow?.assistanceId, selectedRow?.assignTo])

  const assistanceDetailIdForSelectedRow = useMemo(() => {
    const row = selectedRow ?? selectedActionRow
    if (!row) return ''

    return row.assistanceId || row.id || ''
  }, [selectedRow, selectedActionRow])

  const selectedAssignmentRequest = useMemo(() => {
    if (!selectedRow) return null

    const timelineRequest = (callsignsQuery.data ?? [])
      .flatMap((callsign) => callsign.requests)
      .find((request) => request.id === assistanceDetailIdForSelectedRow)

    return {
      id: assistanceDetailIdForSelectedRow,
      startTime: timelineRequest?.startTime ?? selectedRow.time,
      endTime: timelineRequest?.endTime ?? selectedRow.time,
    }
  }, [assistanceDetailIdForSelectedRow, callsignsQuery.data, selectedRow])

  const isAssignmentDataLoading = callsignsQuery.isPending || callsignsQuery.isFetching

  const handleAssign = (assistanceDetailId: string, callsignId: string) => {
    const effectiveId = assistanceDetailId || assistanceDetailIdForSelectedRow
    const callsign = modalCallsigns.find((candidate) => candidate.id === callsignId)
    const callsignName = callsign?.name.trim() || callsign?.staffName?.trim()
    if (!callsignName) return

    assignMutation.mutate({
      assistanceDetailId: effectiveId,
      callsignId,
      callsignName,
      staffName: callsign?.staffName?.trim() || undefined,
      isReassignment: Boolean(selectedRow?.assignTo),
    })
  }

  const handleUnassign = (assistanceDetailId: string) => {
    const assignedCallsignId = resolveAssignedCallsignId(
      [...callsigns, ...(callsignDisplayRowsQuery.data ?? [])],
      assistanceDetailId,
      selectedRow?.assignTo
    )

    if (!assignedCallsignId) return
    unassignMutation.mutate({ callsignId: assignedCallsignId, assistanceDetailId })
  }

  const errorMessage =
    error instanceof Response
      ? `Request failed (${error.status})`
      : error instanceof Error
        ? error.message
        : error
          ? String(error)
          : null

  const applyFilters = (nextFilters: DashboardRequestsFilters) => {
    if (!selectedStationCode) {
      setAppliedFilters(null)
      return
    }

    const dateFromParam = buildDateTimeParam(nextFilters.date_from)
    const dateToParam = buildDateTimeParam(nextFilters.date_to)

    setAppliedFilters({
      ...nextFilters,
      date_from: dateFromParam,
      date_to: dateToParam,
      station_code: selectedStationCode,
    })
  }

  const handleSearch = () => {
    applyFilters(filters)
    trackEvent('filter_applied', { area: 'requests', filter_type: 'search' })
  }

  const handleClear = () => {
    clearFilters()
    setAppliedFilters(null)
  }

  const applyPreset = (preset: 'tomorrow' | '7days' | '30days') => {
    const now = new Date()
    let dateFrom: Date
    let dateTo: Date

    if (preset === 'tomorrow') {
      dateFrom = addDays(now, 1)
      dateFrom.setHours(0, 0, 0, 0)
      dateTo = addDays(now, 1)
      dateTo.setHours(23, 59, 0, 0)
    } else if (preset === '7days') {
      dateFrom = new Date(now)
      dateFrom.setHours(0, 0, 0, 0)
      dateTo = addDays(now, 7)
      dateTo.setHours(23, 59, 0, 0)
    } else {
      dateFrom = new Date(now)
      dateFrom.setHours(0, 0, 0, 0)
      dateTo = addDays(now, 30)
      dateTo.setHours(23, 59, 0, 0)
    }

    const fromStr = format(dateFrom, "yyyy-MM-dd'T'HH:mm")
    const toStr = format(dateTo, "yyyy-MM-dd'T'HH:mm")
    setFilters({ date_from: fromStr, date_to: toStr })
    applyFilters({ ...filters, date_from: fromStr, date_to: toStr })
    trackEvent('filter_applied', { area: 'requests', filter_type: 'preset' })
  }

  // Sync category changes from GlobalMenuBar sub-nav into applied filters
  useEffect(() => {
    setAppliedFilters((current) => (current ? { ...current, category: filters.category } : current))
  }, [filters.category])

  useEffect(() => {
    if (!selectedStationCode) return
    if (lastAutoLoadedStationRef.current === selectedStationCode) return

    setAppliedFilters({
      ...filters,
      date_from: buildDateTimeParam(filters.date_from),
      date_to: buildDateTimeParam(filters.date_to),
      station_code: selectedStationCode,
    })
    lastAutoLoadedStationRef.current = selectedStationCode
  }, [selectedStationCode, filters])

  return (
    <>
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <div className="min-w-xs flex-[2] shrink-0">
            <label htmlFor="filter-query" className="mb-1 block text-xs font-medium text-gray-700">
              {t('dashboard.searchLabel')}
            </label>
            <input
              id="filter-query"
              type="search"
              value={filters.query}
              onChange={(e) => setFilter('query', e.target.value)}
              placeholder={t('dashboard.searchPlaceholder')}
              className="w-full rounded border border-border-gray px-2 py-1.5 text-sm"
            />
          </div>
          <div className="min-w-44 flex-1 shrink-0">
            <label
              htmlFor="filter-start-date"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              {t('dashboard.startDate')}
            </label>
            <input
              id="filter-start-date"
              type="datetime-local"
              value={filters.date_from}
              onChange={(e) => setFilter('date_from', e.target.value)}
              className="w-full rounded border border-border-gray px-2 py-1.5 text-sm"
            />
          </div>
          <div className="min-w-44 flex-1 shrink-0">
            <label
              htmlFor="filter-end-date"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              {t('dashboard.endDate')}
            </label>
            <input
              id="filter-end-date"
              type="datetime-local"
              value={filters.date_to}
              onChange={(e) => setFilter('date_to', e.target.value)}
              className="w-full rounded border border-border-gray px-2 py-1.5 text-sm"
            />
          </div>
          <div className="shrink-0">
            <p className="mb-1 block text-xs font-medium text-gray-700">
              {t('dashboard.presets.label')}
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => applyPreset('tomorrow')}
                disabled={!selectedStationCode}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('dashboard.presets.tomorrow')}
              </button>
              <button
                type="button"
                onClick={() => applyPreset('7days')}
                disabled={!selectedStationCode}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('dashboard.presets.sevenDays')}
              </button>
              <button
                type="button"
                onClick={() => applyPreset('30days')}
                disabled={!selectedStationCode}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('dashboard.presets.thirtyDays')}
              </button>
            </div>
          </div>
          <div className="ml-auto flex shrink-0 gap-2 self-end">
            <AppButton
              variant="primary"
              size="sm"
              onClick={handleSearch}
              disabled={isLoading || !selectedStationCode}
              loading={isLoading}
            >
              {t('dashboard.search')}
            </AppButton>
            <AppButton variant="secondary" size="sm" onClick={handleClear}>
              {t('dashboard.clear')}
            </AppButton>
          </div>
        </div>
        {!selectedStationCode && (
          <p className="mt-2 text-xs text-gray-500">{t('common.selectStationFirst')}</p>
        )}
      </div>

      <div className="space-y-2 p-4">
        <p className="sr-only" role="status" aria-live="polite">
          {isLoading
            ? t('common.loading')
            : t('dashboard.resultsCount', { count: sortedBookings.length })}
        </p>
        {errorMessage && (
          <div
            role="alert"
            className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {errorMessage}
          </div>
        )}

        {isLoading ? (
          <DashboardBookingsTableSkeleton columns={BOOKING_COLUMNS.length} />
        ) : (
          <DataTable
            columns={BOOKING_COLUMNS.map((column) => {
              const columnKey = String(column.key)
              const translatedHeader: React.ReactNode =
                typeof column.header === 'string'
                  ? t(column.header)
                  : (column.header as React.ReactNode)

              if (isSortableBookingColumnKey(columnKey)) {
                const sortDirection = sortState?.key === columnKey ? sortState.direction : null
                const sortIndicator =
                  sortDirection === 'asc' ? '^' : sortDirection === 'desc' ? 'v' : ''

                return {
                  ...column,
                  ariaSort:
                    sortDirection === 'asc'
                      ? ('ascending' as const)
                      : sortDirection === 'desc'
                        ? ('descending' as const)
                        : ('none' as const),
                  header: (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-left"
                      aria-label={`${String(translatedHeader)}. ${
                        sortDirection === 'asc'
                          ? t('dashboard.sortDescending')
                          : t('dashboard.sortAscending')
                      }`}
                      onClick={() => {
                        setSortState((current) => {
                          if (!current || current.key !== columnKey) {
                            return { key: columnKey, direction: 'asc' }
                          }

                          return {
                            key: columnKey,
                            direction: current.direction === 'asc' ? 'desc' : 'asc',
                          }
                        })
                      }}
                    >
                      <span>{translatedHeader}</span>
                      <span aria-hidden="true">{sortIndicator}</span>
                    </button>
                  ),
                }
              }

              if (column.key !== 'assignTo') {
                return {
                  ...column,
                  header: translatedHeader,
                }
              }

              return {
                ...column,
                header: translatedHeader,
                render: (row: BookingRow) => (
                  <div className="flex w-full items-center">
                    {row.assignTo ? (
                      <button
                        type="button"
                        aria-label={`${t('dashboard.assignment.reassignAction')} ${row.assignTo}`}
                        className="inline-flex w-full min-w-0 items-center justify-between gap-2 text-xs text-gray-900"
                        onClick={() => openReassignFlowForRow(row)}
                      >
                        <span
                          className="max-w-[20ch] truncate text-left font-normal text-gray-700"
                          title={row.assignToStaffName || undefined}
                        >
                          {row.assignToStaffName ?? '-'}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1 font-bold whitespace-nowrap text-gray-900">
                          <span>{row.assignTo}</span>
                          <RefreshCw size={14} aria-hidden="true" className="size-3.5" />
                        </span>
                      </button>
                    ) : (
                      <div className="flex w-full justify-center">
                        <AppButton
                          variant="primary"
                          size="sm"
                          buttonClass={TABLE_ACTION_BUTTON_CLASS}
                          onClick={() => openAssignFlowForRow(row)}
                        >
                          {t('dashboard.assignment.assignAction')}
                        </AppButton>
                      </div>
                    )}
                  </div>
                ),
              }
            })}
            data={sortedBookings}
            ariaLabel={t('dashboard.requestsTableLabel')}
            tableClassName="[&_th]:px-1.5 [&_td]:px-1.5"
            emptyMessage={t('dashboard.noBookingsMessage')}
            getRowKey={(row) => row.id}
            headerClassNameByKey={{
              paRef: 'text-center',
              passenger: 'text-center',
              requestType: 'text-center',
              fromCode: 'text-center',
              toCode: 'text-center',
              journeyDate: 'text-center',
              time: 'text-center',
              platform: 'text-center',
              headcode: 'text-center',
              rsid: 'text-center',
              assistanceDetails: 'text-center',
              assignTo: 'text-center',
              status: 'text-center',
              requestChecklist: 'text-center',
              actions: 'text-center',
            }}
            cellClassNameByKey={{
              paRef: 'text-center align-middle',
              passenger: 'text-center align-middle',
              requestType: 'text-center align-middle',
              fromCode: 'text-center align-middle',
              toCode: 'text-center align-middle',
              journeyDate: 'text-center align-middle',
              time: 'text-center align-middle',
              platform: 'text-center align-middle',
              headcode: 'text-center align-middle',
              rsid: 'text-center align-middle',
              assistanceDetails: 'text-center align-middle',
              assignTo: 'align-middle',
              status: 'text-center align-middle',
              requestChecklist: 'text-center align-middle whitespace-nowrap',
              actions: 'text-center align-middle',
            }}
          />
        )}
      </div>

      <AddAssistanceNoteModal
        isOpen={isAddNoteOpen}
        onClose={() => {
          setIsAddNoteOpen(false)
          setNoteBookingId('')
        }}
        bookingLog={bookingLogQuery.data ?? []}
        isBookingLogLoading={bookingLogQuery.isPending}
        bookingLogError={bookingLogQuery.error}
        onAddNote={(text) => {
          if (!bookingId || !selectedStationCode) return
          createBookingNoteMutation.mutate({
            id: bookingId,
            data: { text },
            params: { station_code: selectedStationCode },
          })
        }}
      />

      <EmailManagerModal
        area="requests"
        isOpen={isEmailManagerOpen}
        onClose={() => {
          setIsEmailManagerOpen(false)
        }}
        assistanceId={assistanceDetailIdForSelectedRow}
      />

      <CompleteChecklistModal
        area="requests"
        isOpen={isChecklistOpen}
        onClose={() => {
          setIsChecklistOpen(false)
          setChecklistRequest(null)
        }}
        request={checklistRequest}
      />

      <ValidationReminderModal
        isOpen={isValidationReminderOpen}
        onCancel={() => {
          setIsValidationReminderOpen(false)
          setSelectedRow(null)
        }}
        onContinue={() => {
          setIsValidationReminderOpen(false)
          setIsAssignModalOpen(true)
        }}
      />

      <RequestManagementModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false)
          setSelectedRow(null)
        }}
        request={selectedAssignmentRequest}
        callsigns={modalCallsigns}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        title={t('dashboard.assignment.assignToTitle')}
        prompt={t('dashboard.assignment.selectCallsignPrompt')}
        confirmLabel={t('dashboard.assignment.assignAction')}
        isAssigned={Boolean(selectedRow?.assignTo)}
        isCallsignsLoading={isAssignmentDataLoading}
        hideUnassignConfirmCloseButton
      />

      <RequestManagementModal
        isOpen={isReassignModalOpen}
        onClose={() => {
          setIsReassignModalOpen(false)
          setSelectedRow(null)
        }}
        request={selectedAssignmentRequest}
        callsigns={modalCallsigns}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        title={t('dashboard.assignment.reassignTitle')}
        prompt={t('dashboard.assignment.selectCallsignPrompt')}
        confirmLabel={t('dashboard.assignment.reassignAction')}
        initialCallsignId={currentCallsignIdForSelectedRow}
        isAssigned={Boolean(selectedRow?.assignTo)}
        isCallsignsLoading={isAssignmentDataLoading}
        hideUnassignConfirmCloseButton
      />
    </>
  )
}
